// src/components/table/useTableController.js
import { useMemo, useState } from "react";

/**
 * Client-side search, filter, sort, pagination.
 * NOW supports server pagination without breaking existing usage.
 *
 * @param {Array} items - raw list from API (client: full list, server: current page list)
 * @param {Object} opts
 *  - searchKeys: string[] -> keys to search in
 *  - initialPageSize: number
 *  - normalizers: optional map of key -> (val)=>normalizedVal
 *  - pagination: {
 *      mode?: "client" | "server"   // default "client"
 *      total?: number              // required for server mode to compute totalPages
 *    }
 */
export default function useTableController(items = [], opts = {}) {
  const {
    searchKeys = [],
    initialPageSize = 10,
    normalizers = {},
    pagination = {},
  } = opts;

  const mode = pagination?.mode || "client";
  const externalTotal =
    mode === "server" && Number.isFinite(Number(pagination?.total))
      ? Number(pagination.total)
      : null;

  // UI state
  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState({}); // {key:value}
  const [sortKey, setSortKey] = useState(null); // string | null
  const [sortDir, setSortDir] = useState("asc"); // 'asc' | 'desc'
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(initialPageSize);

  // helpers
  const norm = (k, v) => (normalizers[k] ? normalizers[k](v) : v);

  const processed = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();

    // 1) search
    let out = items.filter((row) => {
      if (!q) return true;
      if (!searchKeys.length) return true;
      return searchKeys.some((k) => {
        const val = row?.[k];
        return String(val ?? "").toLowerCase().includes(q);
      });
    });

    // 2) filters (each must match)
    out = out.filter((row) => {
      for (const k in filters) {
        const want = filters[k];
        if (want === undefined || want === "" || want === "all") continue;
        const got = norm(k, String(row?.[k] ?? "").toLowerCase());
        const need = norm(k, String(want).toLowerCase());
        if (got !== need) return false;
      }
      return true;
    });

    // 3) sort (still allowed; in server mode this sorts CURRENT PAGE only)
    if (sortKey) {
      const dir = sortDir === "desc" ? -1 : 1;
      out = [...out].sort((a, b) => {
        const av = a?.[sortKey],
          bv = b?.[sortKey];
        const an = av instanceof Date ? av.getTime() : av;
        const bn = bv instanceof Date ? bv.getTime() : bv;
        if (an == null && bn == null) return 0;
        if (an == null) return -1 * dir;
        if (bn == null) return 1 * dir;
        if (typeof an === "number" && typeof bn === "number")
          return (an - bn) * dir;
        return String(an).localeCompare(String(bn)) * dir;
      });
    }

    return out;
  }, [items, searchTerm, filters, sortKey, sortDir, searchKeys, normalizers]);

  // 4) pagination
  // ✅ client mode: total = processed.length and slice rows
  // ✅ server mode: total = externalTotal and DO NOT slice rows (backend already paginated)
  const total = mode === "server" && externalTotal != null ? externalTotal : processed.length;

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  // ✅ IMPORTANT:
  // - client: clamp by totalPages computed from processed.length
  // - server: clamp by totalPages computed from externalTotal (NOT by processed.length)
  const safePage = Math.min(Math.max(1, page), totalPages);

  const startIndex = (safePage - 1) * pageSize;

  const rows =
    mode === "server"
      ? processed // server already paginated; processed is "current page" list (optionally searched)
      : processed.slice(startIndex, startIndex + pageSize);

  // public API
  const toggleSort = (key) => {
    if (!key) return;
    if (sortKey !== key) {
      setSortKey(key);
      setSortDir("asc");
    } else {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    }
  };

  const setFilter = (key, value) => {
    setPage(1);
    setFilters((f) => ({ ...f, [key]: value }));
  };

  const setSearch = (val) => {
    setPage(1);
    setSearchTerm(val);
  };

  const onPageSize = (val) => {
    setPage(1);
    setPageSize(Number(val));
  };

  return {
    // data
    rows,
    total,
    totalPages,
    startIndex, // helpful for S.N. in server mode too

    // pagination
    page: safePage,
    setPage,
    pageSize,
    setPageSize: onPageSize,

    // search
    searchTerm,
    setSearch,

    // filters
    filters,
    setFilter,

    // sort
    sortKey,
    sortDir,
    toggleSort,
  };
}
